/**
 * Admin — archive chat global & tables / brouillons.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';
import AdminSelect from '../../components/AdminSelect';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { TrashIcon } from '../../components/Icons';
import MessageQuoteBlock from '../../components/chat/MessageQuoteBlock';
import { formatChatTime } from '../../utils/chatMessage';

interface GlobalChatLogPageProps {
  session: Session;
}

interface ChatLogMessage {
  id: number;
  messageId: string;
  roomId: string;
  roomLabel: string;
  nature?: string | null;
  natureLabel?: string | null;
  tableTitle?: string | null;
  participants?: Array<{ pseudo: string; position?: number; seat?: string; tableIndex?: number }>;
  participantsLine?: string | null;
  roomKind?: string;
  pseudo: string;
  text: string;
  messageType: string;
  source: string | null;
  quoteAuthor?: string | null;
  quoteText?: string | null;
  sentAt: string;
  moderatedAt?: string | null;
  moderatedBy?: string | null;
  moderatedReason?: string | null;
  originalText?: string | null;
  archiveKind?: 'global' | 'table';
}

interface ActiveSanction {
  id: number;
  pseudo: string;
  type: string;
  scope: string;
  reason: string | null;
  evidenceMessageId?: string | null;
  issuedBy: string;
  startsAt: string;
  expiresAt: string | null;
}

type EvidenceStatus = 'active' | 'expired' | 'revoked';

interface ContextIsland {
  key: string;
  roomId: string;
  roomLabel: string;
  natureLabel?: string | null;
  tableTitle?: string | null;
  participantsLine?: string | null;
  stackLabel?: string | null;
  messages: ChatLogMessage[];
  /** Keys roomId::messageId */
  hitIds: Set<string>;
  gapBeforeMs?: number;
}

type KindFilter = 'all' | 'global' | 'table';
type RoomFilter = 'all' | 'global:detente' | 'global:compet' | string;
type TableKindFilter = 'all' | 'neo' | 'legacy' | 'draft';
type NatureFilter =
  | 'all'
  | 'libre'
  | 'defi_libre'
  | 'live'
  | 'diff_indiv'
  | 'diff_tq'
  | 'compet_neo'
  | 'cia'
  | 'championnat_equipe'
  | 'defi_par_equipe'
  | 'defi_ouvert'
  | 'defi_quadrette';

function stackLabelFromRoom(roomId: string, roomKind?: string | null): string | null {
  if (roomKind === 'neo' || roomId.startsWith('neo:')) return 'Neo';
  if (roomKind === 'legacy' || roomId.startsWith('table:')) return 'Legacy';
  if (roomKind === 'draft' || roomId.startsWith('draft:')) return 'Brouillon';
  return null;
}

function isTableRoomId(roomId: string): boolean {
  return roomId.startsWith('neo:') || roomId.startsWith('table:') || roomId.startsWith('draft:');
}

function sanctionScopeForMessage(msg: ChatLogMessage): string {
  if (isTableRoomId(msg.roomId)) return 'global:*';
  if (msg.roomId === 'global:compet' || msg.roomId === 'global:detente') return msg.roomId;
  return 'global:*';
}

const CONTEXT_BEFORE = 60;
const CONTEXT_AFTER = 60;
const ISLAND_GAP_MS = 15 * 60 * 1000;
const MAX_CONTEXT_HITS = 40;
const LIVE_POLL_MS = 4000;

const NON_MODERATABLE_TYPES = new Set([
  'game_invite',
  'library_share',
  'cia_agenda_invite',
]);

const SANCTION_TYPE_LABELS: Record<string, string> = {
  warn: 'Avertissement',
  mute: 'Mute',
  premod: 'Prémod',
  channel_ban: 'Ban canal',
  site_ban: 'Ban site',
};

const SCOPE_LABELS: Record<string, string> = {
  'global:detente': 'Détente',
  'global:compet': 'Compétition',
  'global:*': 'Tous salons',
  site: 'Site',
};

function isModeratable(msg: ChatLogMessage): boolean {
  return !NON_MODERATABLE_TYPES.has(msg.messageType || 'text');
}

function sanctionTypeLabel(type: string): string {
  return SANCTION_TYPE_LABELS[type] ?? type;
}

function formatExpires(expiresAt: string | null): string {
  if (!expiresAt) return 'sans expiration';
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return expiresAt;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function adminFetch(url: string, pseudo: string, init?: RequestInit): Promise<Response> {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('pseudo', pseudo);
  return fetch(u.toString(), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Pseudo': pseudo,
      'X-Moderator-Pseudo': pseudo,
      ...(init?.headers || {}),
    },
  });
}

function todayStartLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

function startOfLocalDayOffset(daysBack: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysBack);
  return toLocalDateTimeValue(d);
}

function startOfMonthAgo(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() - 1);
  return toLocalDateTimeValue(d);
}

function parseLocalDateTime(value: string): Date | null {
  if (!value?.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toLocalDateTimeValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function addToFrom(fromValue: string, ms: number): string | null {
  const base = parseLocalDateTime(fromValue);
  if (!base) return null;
  return toLocalDateTimeValue(new Date(base.getTime() + ms));
}

function formatGap(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min omises`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours < 48) return rem > 0 ? `${hours} h ${rem} min omises` : `${hours} h omises`;
  const days = Math.floor(hours / 24);
  return `${days} j omis`;
}

function msgKey(m: Pick<ChatLogMessage, 'roomId' | 'messageId'>): string {
  return `${m.roomId}::${m.messageId}`;
}

function dedupeByMsgKey(messages: ChatLogMessage[]): ChatLogMessage[] {
  const seen = new Set<string>();
  const out: ChatLogMessage[] = [];
  for (const m of messages) {
    const k = msgKey(m);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(m);
  }
  return out;
}

/**
 * Assigne chaque message au groupe d’occurrence le plus proche (évite des îlots
 * quasi-identiques quand les ±60 se chevauchent).
 */
function messagesForHitGroup(
  messages: ChatLogMessage[],
  groupHits: ChatLogMessage[],
  allHits: ChatLogMessage[],
): ChatLogMessage[] {
  const groupKeys = new Set(groupHits.map((h) => msgKey(h)));
  const otherHits = allHits.filter((h) => !groupKeys.has(msgKey(h)));

  return messages.filter((m) => {
    const k = msgKey(m);
    if (groupKeys.has(k)) return true;
    if (otherHits.some((h) => msgKey(h) === k)) return false;

    const t = new Date(m.sentAt).getTime();
    const distGroup = Math.min(
      ...groupHits.map((h) => Math.abs(t - new Date(h.sentAt).getTime())),
    );
    if (otherHits.length === 0) return true;
    const distOther = Math.min(
      ...otherHits.map((h) => Math.abs(t - new Date(h.sentAt).getTime())),
    );
    return distGroup <= distOther;
  });
}

function buildIslands(
  contexts: { hit: ChatLogMessage; messages: ChatLogMessage[] }[],
): ContextIsland[] {
  if (contexts.length === 0) return [];

  // Dédupliquer les hits (évite 2× les mêmes occurrences)
  const byHit = new Map<string, { hit: ChatLogMessage; messages: ChatLogMessage[] }>();
  for (const ctx of contexts) {
    const k = msgKey(ctx.hit);
    const prev = byHit.get(k);
    if (!prev) {
      byHit.set(k, { hit: ctx.hit, messages: [...ctx.messages] });
    } else {
      const merged = new Map<string, ChatLogMessage>();
      for (const m of [...prev.messages, ...ctx.messages, ctx.hit, prev.hit]) {
        merged.set(msgKey(m), m);
      }
      byHit.set(k, { hit: prev.hit, messages: [...merged.values()] });
    }
  }

  const unique = [...byHit.values()].sort((a, b) => {
    if (a.hit.roomId !== b.hit.roomId) return a.hit.roomId.localeCompare(b.hit.roomId);
    return new Date(a.hit.sentAt).getTime() - new Date(b.hit.sentAt).getTime();
  });
  const allHits = unique.map((c) => c.hit);

  const groups: typeof unique[] = [];
  let group: typeof unique = [];

  for (const ctx of unique) {
    if (group.length === 0) {
      group = [ctx];
      continue;
    }
    const prev = group[group.length - 1].hit;
    const sameRoom = prev.roomId === ctx.hit.roomId;
    const gap = new Date(ctx.hit.sentAt).getTime() - new Date(prev.sentAt).getTime();
    if (sameRoom && gap <= ISLAND_GAP_MS) {
      group.push(ctx);
    } else {
      groups.push(group);
      group = [ctx];
    }
  }
  if (group.length > 0) groups.push(group);

  const islands: ContextIsland[] = [];

  for (const g of groups) {
    const hitIds = new Set(g.map((c) => msgKey(c.hit)));
    const groupHits = g.map((c) => c.hit);
    const pool = new Map<string, ChatLogMessage>();
    for (const ctx of g) {
      pool.set(msgKey(ctx.hit), ctx.hit);
      for (const m of ctx.messages) pool.set(msgKey(m), m);
    }

    const messages = messagesForHitGroup([...pool.values()], groupHits, allHits).sort(
      (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
    );
    if (messages.length === 0) continue;

    const firstHit = g[0].hit;
    let gapBeforeMs: number | undefined;
    if (islands.length > 0) {
      const prevIsland = islands[islands.length - 1];
      const prevLast = prevIsland.messages[prevIsland.messages.length - 1];
      if (prevLast.roomId === firstHit.roomId) {
        gapBeforeMs = new Date(messages[0].sentAt).getTime() - new Date(prevLast.sentAt).getTime();
      }
    }

    islands.push({
      key: `${firstHit.roomId}-${g.map((c) => c.hit.messageId).join('-')}`,
      roomId: firstHit.roomId,
      roomLabel: firstHit.roomLabel || messages[0].roomLabel,
      natureLabel: firstHit.natureLabel ?? messages[0].natureLabel ?? null,
      tableTitle: firstHit.tableTitle ?? messages[0].tableTitle ?? null,
      participantsLine: firstHit.participantsLine ?? messages[0].participantsLine ?? null,
      stackLabel: stackLabelFromRoom(firstHit.roomId, firstHit.roomKind ?? messages[0].roomKind),
      messages,
      hitIds,
      gapBeforeMs,
    });
  }

  return islands;
}

function mergeLiveMessages(
  prev: ContextIsland[],
  incoming: ChatLogMessage[],
  asHits: boolean,
): ContextIsland[] {
  if (incoming.length === 0) return prev;
  let next = prev.map((island) => ({
    ...island,
    hitIds: new Set(island.hitIds),
    messages: [...island.messages],
  }));

  for (const msg of incoming) {
    const key = msgKey(msg);
    if (next.some((island) => island.messages.some((m) => msgKey(m) === key))) continue;

    const last = next[next.length - 1];
    const msgTs = new Date(msg.sentAt).getTime();
    if (
      last
      && last.roomId === msg.roomId
      && last.messages.length > 0
      && msgTs - new Date(last.messages[last.messages.length - 1].sentAt).getTime() <= ISLAND_GAP_MS
    ) {
      last.messages.push(msg);
      if (asHits) last.hitIds.add(msgKey(msg));
    } else {
      next = [
        ...next,
        {
          key: `live-${msg.roomId}-${msg.messageId}`,
          roomId: msg.roomId,
          roomLabel: msg.roomLabel,
          natureLabel: msg.natureLabel ?? null,
          tableTitle: msg.tableTitle ?? null,
          participantsLine: msg.participantsLine ?? null,
          stackLabel: stackLabelFromRoom(msg.roomId, msg.roomKind),
          messages: [msg],
          hitIds: asHits ? new Set([msgKey(msg)]) : new Set(),
          gapBeforeMs: last
            ? msgTs - new Date(last.messages[last.messages.length - 1].sentAt).getTime()
            : undefined,
        },
      ];
    }
  }
  return next;
}

function patchMessage(
  list: ChatLogMessage[],
  messageId: string,
  roomId: string,
  patch: Partial<ChatLogMessage>,
): ChatLogMessage[] {
  return list.map((m) =>
    m.messageId === messageId && m.roomId === roomId ? { ...m, ...patch } : m,
  );
}

export default function GlobalChatLogPage({ session }: GlobalChatLogPageProps) {
  const [kind, setKind] = useState<KindFilter>('all');
  const [room, setRoom] = useState<RoomFilter>('all');
  const [tableKind, setTableKind] = useState<TableKindFilter>('all');
  const [nature, setNature] = useState<NatureFilter>('all');
  const [from, setFrom] = useState(todayStartLocal);
  const [to, setTo] = useState('');
  const [toUnlimited, setToUnlimited] = useState(true);
  const [pseudo, setPseudo] = useState('');
  const [q, setQ] = useState('');
  const [q2, setQ2] = useState('');

  const [hits, setHits] = useState<ChatLogMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [islands, setIslands] = useState<ContextIsland[]>([]);
  const [focusHitKey, setFocusHitKey] = useState<string | null>(null);
  const [focusHitIndex, setFocusHitIndex] = useState(0);
  /** pseudo lower → sanctions actives (révocables) */
  const [activeByPseudo, setActiveByPseudo] = useState<Map<string, ActiveSanction[]>>(new Map());
  /** pseudo lower → sanctions expirées (affichage grisé) */
  const [expiredByPseudo, setExpiredByPseudo] = useState<Map<string, ActiveSanction[]>>(new Map());
  /** messageId → active | expired (revoked exclus) */
  const [evidenceByMessageId, setEvidenceByMessageId] = useState<Map<string, EvidenceStatus>>(new Map());
  const [liveOn, setLiveOn] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{
    pseudo: string;
    sanctions: ActiveSanction[];
  } | null>(null);
  const [revoking, setRevoking] = useState(false);

  const [pseudoSuggestions, setPseudoSuggestions] = useState<
    Array<{ pseudo: string; pseudoDisplay: string }>
  >([]);
  const [showPseudoDropdown, setShowPseudoDropdown] = useState(false);
  const [pseudoSearchLoading, setPseudoSearchLoading] = useState(false);
  const pseudoSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pseudoFieldRef = useRef<HTMLDivElement | null>(null);

  const focusRef = useRef<HTMLDivElement | null>(null);
  const newestSentAtRef = useRef<string | null>(null);
  const knownKeysRef = useRef<Set<string>>(new Set());
  const hitKeysRef = useRef<Set<string>>(new Set());

  const limit = 40;
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const hasOccurrenceFilters = Boolean(
    pseudo.trim() || q.trim() || q2.trim(),
  );
  const showOccurrenceNav = hasOccurrenceFilters && hits.length > 1;

  const applyToPreset = (preset: '5m' | '1h' | '1d' | '1w' | 'none') => {
    if (preset === 'none') {
      setTo('');
      setToUnlimited(true);
      return;
    }
    const ms =
      preset === '5m' ? 5 * 60_000
        : preset === '1h' ? 60 * 60_000
          : preset === '1d' ? 24 * 60 * 60_000
            : 7 * 24 * 60 * 60_000;
    const next = addToFrom(from, ms);
    if (next) {
      setTo(next);
      setToUnlimited(false);
    }
  };

  const applyFromPreset = (preset: 'today' | 'yesterday' | 'week' | 'month') => {
    if (preset === 'today') setFrom(todayStartLocal());
    else if (preset === 'yesterday') setFrom(startOfLocalDayOffset(1));
    else if (preset === 'week') setFrom(startOfLocalDayOffset(7));
    else setFrom(startOfMonthAgo());
  };

  const searchUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('kind', kind);
    if (room !== 'all') params.set('room', room);
    if ((kind === 'table' || kind === 'all') && tableKind !== 'all') params.set('roomKind', tableKind);
    if ((kind === 'table' || kind === 'all') && nature !== 'all') params.set('nature', nature);
    if (from) params.set('from', from);
    if (!toUnlimited && to) params.set('to', to);
    if (pseudo.trim()) params.set('pseudo', pseudo.trim());
    if (q.trim()) params.set('q', q.trim());
    if (q2.trim()) params.set('q2', q2.trim());
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return `${BACKEND_URL}/api/admin/chat-log/search?${params.toString()}`;
  }, [kind, room, tableKind, nature, from, to, toUnlimited, pseudo, q, q2, offset]);

  const refreshSanctions = useCallback(async () => {
    try {
      const evidenceRes = await adminFetch(
        `${BACKEND_URL}/api/moderation/evidence-messages`,
        session.pseudo,
      );
      if (!evidenceRes.ok) return;
      const data = await evidenceRes.json();

      const sanctions = (data.sanctions ?? []) as ActiveSanction[];
      const activeMap = new Map<string, ActiveSanction[]>();
      const expiredMap = new Map<string, ActiveSanction[]>();
      const now = Date.now();
      for (const s of sanctions) {
        if (!['mute', 'channel_ban', 'site_ban', 'premod'].includes(s.type)) continue;
        const key = s.pseudo.toLowerCase();
        const expired = s.expiresAt != null && new Date(s.expiresAt).getTime() <= now;
        const map = expired ? expiredMap : activeMap;
        const list = map.get(key) ?? [];
        list.push(s);
        map.set(key, list);
      }
      setActiveByPseudo(activeMap);
      setExpiredByPseudo(expiredMap);

      const evidenceMap = new Map<string, EvidenceStatus>();
      for (const e of (data.evidence ?? []) as Array<{ messageId: string; status: EvidenceStatus }>) {
        if (e.status === 'revoked') continue;
        evidenceMap.set(e.messageId, e.status);
      }
      setEvidenceByMessageId(evidenceMap);
    } catch {
      // ignore
    }
  }, [session.pseudo]);

  const searchPseudos = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setPseudoSuggestions([]);
      return;
    }
    setPseudoSearchLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(query.trim())}&limit=8&compet=true`,
      );
      if (!res.ok) {
        setPseudoSuggestions([]);
        return;
      }
      const data = await res.json();
      const players = (data.players ?? data.results ?? data ?? []) as Array<{
        pseudo?: string;
        pseudoDisplay?: string;
      }>;
      setPseudoSuggestions(
        players
          .filter((p) => p.pseudo)
          .map((p) => ({
            pseudo: p.pseudo!,
            pseudoDisplay: p.pseudoDisplay || p.pseudo!,
          })),
      );
    } catch {
      setPseudoSuggestions([]);
    } finally {
      setPseudoSearchLoading(false);
    }
  }, []);

  const onPseudoInput = (value: string) => {
    setPseudo(value);
    if (pseudoSearchTimer.current) clearTimeout(pseudoSearchTimer.current);
    pseudoSearchTimer.current = setTimeout(() => {
      void searchPseudos(value);
      setShowPseudoDropdown(true);
    }, 220);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!pseudoFieldRef.current?.contains(e.target as Node)) {
        setShowPseudoDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadContextsForHits = useCallback(async (hitList: ChatLogMessage[]) => {
    if (hitList.length === 0) {
      setIslands([]);
      knownKeysRef.current = new Set();
      newestSentAtRef.current = null;
      return;
    }
    setContextLoading(true);
    try {
      const slice = hitList.slice(0, MAX_CONTEXT_HITS);
      const contexts = await Promise.all(
        slice.map(async (hit) => {
          const params = new URLSearchParams({
            room: hit.roomId,
            messageId: hit.messageId,
            before: String(CONTEXT_BEFORE),
            after: String(CONTEXT_AFTER),
          });
          const res = await adminFetch(
            `${BACKEND_URL}/api/admin/chat-log/context?${params}`,
            session.pseudo,
          );
          if (!res.ok) return { hit, messages: [hit] as ChatLogMessage[] };
          const data = await res.json();
          return { hit, messages: (data.messages ?? [hit]) as ChatLogMessage[] };
        }),
      );
      const built = buildIslands(contexts);
      setIslands(built);
      const keys = new Set<string>();
      let newest: string | null = null;
      for (const island of built) {
        for (const m of island.messages) {
          keys.add(msgKey(m));
          if (!newest || m.sentAt > newest) newest = m.sentAt;
        }
      }
      knownKeysRef.current = keys;
      newestSentAtRef.current = newest;
    } finally {
      setContextLoading(false);
    }
  }, [session.pseudo]);

  const runSearch = useCallback(async (nextOffset = 0) => {
    setLoading(true);
    setError(null);
    setSearched(true);
    setFocusHitKey(null);
    setFocusHitIndex(0);
    try {
      const params = new URLSearchParams(searchUrl.split('?')[1] ?? '');
      params.set('offset', String(nextOffset));
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/chat-log/search?${params.toString()}`,
        session.pseudo,
      );
      if (!res.ok) {
        setError(`Erreur HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      const nextHits = dedupeByMsgKey((data.messages ?? []) as ChatLogMessage[]);
      const chronological = [...nextHits].reverse();
      setHits(chronological);
      hitKeysRef.current = new Set(chronological.map((h) => msgKey(h)));
      setTotal(typeof data.total === 'number' ? data.total : chronological.length);
      setOffset(nextOffset);
      if (chronological.length > 0 && hasOccurrenceFilters) {
        setFocusHitKey(msgKey(chronological[0]));
        setFocusHitIndex(0);
      }
      await loadContextsForHits(chronological);
      await refreshSanctions();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [searchUrl, session.pseudo, loadContextsForHits, refreshSanctions, hasOccurrenceFilters]);

  useEffect(() => {
    void runSearch(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void refreshSanctions();
    const id = window.setInterval(() => void refreshSanctions(), 30_000);
    return () => window.clearInterval(id);
  }, [refreshSanctions]);

  useEffect(() => {
    if (focusHitKey && focusRef.current) {
      focusRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [focusHitKey, islands]);

  useEffect(() => {
    if (!toUnlimited || !searched) {
      setLiveOn(false);
      return;
    }
    setLiveOn(true);
    let cancelled = false;

    const poll = async () => {
      if (cancelled || document.hidden) return;
      const since = newestSentAtRef.current;
      if (!since) return;

      const params = new URLSearchParams();
      params.set('kind', kind);
      if (room !== 'all') params.set('room', room);
      if ((kind === 'table' || kind === 'all') && tableKind !== 'all') params.set('roomKind', tableKind);
      if ((kind === 'table' || kind === 'all') && nature !== 'all') params.set('nature', nature);
      const sinceDate = new Date(since);
      sinceDate.setMilliseconds(sinceDate.getMilliseconds() + 1);
      params.set('from', sinceDate.toISOString());
      if (pseudo.trim()) params.set('pseudo', pseudo.trim());
      if (q.trim()) params.set('q', q.trim());
      if (q2.trim()) params.set('q2', q2.trim());
      params.set('limit', '50');
      params.set('offset', '0');

      try {
        const res = await adminFetch(
          `${BACKEND_URL}/api/admin/chat-log/search?${params}`,
          session.pseudo,
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const fresh = dedupeByMsgKey(
          ((data.messages ?? []) as ChatLogMessage[])
            .filter((m) => !knownKeysRef.current.has(msgKey(m)))
            .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()),
        );
        if (fresh.length === 0) return;

        for (const m of fresh) knownKeysRef.current.add(msgKey(m));
        const newest = fresh[fresh.length - 1].sentAt;
        if (!newestSentAtRef.current || newest > newestSentAtRef.current) {
          newestSentAtRef.current = newest;
        }

        setIslands((prev) => mergeLiveMessages(prev, fresh, true));
        if (hasOccurrenceFilters) {
          const added = fresh.filter((m) => !hitKeysRef.current.has(msgKey(m)));
          if (added.length > 0) {
            for (const m of added) hitKeysRef.current.add(msgKey(m));
            setHits((prev) => [...prev, ...added]);
            setTotal((t) => t + added.length);
          }
        } else {
          setTotal((t) => t + fresh.length);
        }
      } catch {
        // ignore
      }
    };

    const id = window.setInterval(() => void poll(), LIVE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      setLiveOn(false);
    };
  }, [
    toUnlimited,
    searched,
    kind,
    room,
    tableKind,
    nature,
    pseudo,
    q,
    q2,
    session.pseudo,
    hasOccurrenceFilters,
  ]);

  const goToHit = (index: number) => {
    if (hits.length === 0) return;
    const next = Math.max(0, Math.min(hits.length - 1, index));
    setFocusHitIndex(next);
    setFocusHitKey(msgKey(hits[next]));
  };

  const applyMessagePatch = (messageId: string, roomId: string, patch: Partial<ChatLogMessage>) => {
    setHits((prev) => patchMessage(prev, messageId, roomId, patch));
    setIslands((prev) =>
      prev.map((island) => ({
        ...island,
        messages: patchMessage(island.messages, messageId, roomId, patch),
      })),
    );
  };

  const muteUser = async (msg: ChatLogMessage, minutes: number) => {
    const res = await adminFetch(`${BACKEND_URL}/api/moderation/sanctions`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify({
        target: msg.pseudo,
        type: 'mute',
        scope: sanctionScopeForMessage(msg),
        durationMinutes: minutes,
        reason: `Mute depuis log chat`,
        evidenceMessageId: msg.messageId,
      }),
    });
    if (res.ok) {
      void refreshSanctions();
    }
  };

  const banChannel = async (msg: ChatLogMessage) => {
    const res = await adminFetch(`${BACKEND_URL}/api/moderation/sanctions`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify({
        target: msg.pseudo,
        type: 'channel_ban',
        scope: sanctionScopeForMessage(msg),
        durationMinutes: 60 * 24 * 7,
        reason: `Ban canal depuis log chat`,
        evidenceMessageId: msg.messageId,
      }),
    });
    if (res.ok) {
      void refreshSanctions();
    }
  };

  const redactMessage = async (msg: ChatLogMessage) => {
    const res = await adminFetch(`${BACKEND_URL}/api/moderation/messages/redact`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify({
        roomId: msg.roomId,
        messageId: msg.messageId,
        reason: 'Modération manuelle',
      }),
    });
    if (res.ok) {
      applyMessagePatch(msg.messageId, msg.roomId, {
        moderatedAt: new Date().toISOString(),
        moderatedBy: session.pseudo,
        moderatedReason: 'Modération manuelle',
        originalText: msg.originalText ?? msg.text,
      });
    }
  };

  const unredactMessage = async (msg: ChatLogMessage) => {
    const res = await adminFetch(`${BACKEND_URL}/api/moderation/messages/unredact`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify({
        roomId: msg.roomId,
        messageId: msg.messageId,
      }),
    });
    if (res.ok) {
      applyMessagePatch(msg.messageId, msg.roomId, {
        moderatedAt: null,
        moderatedBy: null,
        moderatedReason: null,
      });
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      for (const s of revokeTarget.sanctions) {
        await adminFetch(
          `${BACKEND_URL}/api/moderation/sanctions/${s.id}/revoke`,
          session.pseudo,
          { method: 'POST', body: JSON.stringify({}) },
        );
      }
      setRevokeTarget(null);
      await refreshSanctions();
    } finally {
      setRevoking(false);
    }
  };

  const openRevokeModal = (playerPseudo: string) => {
    const sanctions = activeByPseudo.get(playerPseudo.toLowerCase()) ?? [];
    if (sanctions.length === 0) return;
    setRevokeTarget({ pseudo: playerPseudo, sanctions });
  };

  return (
    <div className="admin-chat-log">
      <p className="admin-chat-log-intro">
        Log chat global (90 j) et tables / brouillons (15 j).
        La recherche charge le contexte autour de chaque résultat (±{CONTEXT_BEFORE} msg).
        Survolez un message texte trouvé pour mute / ban / effacer.
        {liveOn && (
          <>
            {' '}
            <span className="admin-chat-live-badge">● Live</span>
            {' '}nouveaux messages affichés automatiquement (Au sans limite).
          </>
        )}
      </p>

      <form
        className="admin-chat-log-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setShowPseudoDropdown(false);
          void runSearch(0);
        }}
      >
        <div className="admin-chat-log-filter-grid">
          <label className="admin-moderation-field">
            <span>Type</span>
            <AdminSelect
              aria-label="Type d’archive"
              value={kind}
              options={[
                { value: 'all', label: 'Global + tables' },
                { value: 'global', label: 'Chat global' },
                { value: 'table', label: 'Tables / brouillons' },
              ]}
              onChange={(v) => {
                setKind(v as KindFilter);
                setRoom('all');
              }}
            />
          </label>
          <label className="admin-moderation-field">
            <span>Salon</span>
            {kind === 'table' ? (
              <AdminSelect
                aria-label="Type de table"
                value={tableKind}
                options={[
                  { value: 'all', label: 'Neo + legacy + brouillons' },
                  { value: 'neo', label: 'Tables Neo' },
                  { value: 'legacy', label: 'Tables legacy' },
                  { value: 'draft', label: 'Brouillons' },
                ]}
                onChange={(v) => setTableKind(v as TableKindFilter)}
              />
            ) : (
              <AdminSelect
                aria-label="Salon"
                value={room === 'all' || room === 'global:detente' || room === 'global:compet' ? room : 'all'}
                options={[
                  { value: 'all', label: kind === 'global' ? 'Détente + compétition' : 'Tous salons' },
                  { value: 'global:detente', label: 'Détente' },
                  { value: 'global:compet', label: 'Compétition' },
                ]}
                onChange={(v) => setRoom(v as RoomFilter)}
              />
            )}
          </label>
          {(kind === 'table' || kind === 'all') && (
            <label className="admin-moderation-field">
              <span>Nature</span>
              <AdminSelect
                aria-label="Nature de partie"
                value={nature}
                options={[
                  { value: 'all', label: 'Toutes natures' },
                  { value: 'libre', label: 'Partie libre' },
                  { value: 'defi_libre', label: 'Défi libre' },
                  { value: 'cia', label: 'CIA' },
                  { value: 'championnat_equipe', label: 'Championnat par équipe' },
                  { value: 'defi_par_equipe', label: 'Défi par équipe' },
                  { value: 'defi_ouvert', label: 'Défi ouvert' },
                  { value: 'defi_quadrette', label: 'Défi quadrette' },
                  { value: 'live', label: 'Live' },
                  { value: 'diff_indiv', label: 'Différé individuel' },
                  { value: 'diff_tq', label: 'Différé équipes' },
                  { value: 'compet_neo', label: 'Compétition Neo' },
                ]}
                onChange={(v) => setNature(v as NatureFilter)}
              />
            </label>
          )}
          <div className="admin-moderation-field admin-chat-from-field">
            <span>Du</span>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              step={60}
            />
            <div className="admin-chat-presets" role="group" aria-label="Raccourcis début de période">
              {(
                [
                  ['today', 'Auj'],
                  ['yesterday', 'Hier'],
                  ['week', 'Sem'],
                  ['month', 'Mois'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="admin-chat-to-preset"
                  title={
                    key === 'today' ? 'Aujourd’hui 0h'
                      : key === 'yesterday' ? 'Hier 0h'
                        : key === 'week' ? 'Il y a 7 jours 0h'
                          : 'Il y a un mois 0h'
                  }
                  onClick={() => applyFromPreset(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="admin-moderation-field admin-chat-to-field">
            <span>Au</span>
            {toUnlimited ? (
              <div className="admin-chat-to-unlimited" aria-live="polite">
                Sans limite
              </div>
            ) : (
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setToUnlimited(false);
                }}
                step={60}
              />
            )}
            <div className="admin-chat-presets" role="group" aria-label="Raccourcis fin de période">
              {(
                [
                  ['5m', '+5 min'],
                  ['1h', '+1 h'],
                  ['1d', '+1 j'],
                  ['1w', '+1 sem'],
                  ['none', '∞'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`admin-chat-to-preset ${
                    key === 'none' && toUnlimited ? 'active' : ''
                  }`}
                  title={
                    key === 'none'
                      ? 'Sans limite (aucune borne de fin)'
                      : `Définir « Au » = Du ${label}`
                  }
                  onClick={() => applyToPreset(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="admin-moderation-field" ref={pseudoFieldRef}>
            <span>Pseudo</span>
            <div className="admin-chat-pseudo-wrap">
              <input
                value={pseudo}
                onChange={(e) => onPseudoInput(e.target.value)}
                onFocus={() => {
                  if (pseudo.trim().length >= 2) {
                    void searchPseudos(pseudo);
                    setShowPseudoDropdown(true);
                  }
                }}
                placeholder="Contient… (autocomplete)"
                autoComplete="off"
              />
              {pseudoSearchLoading && <span className="admin-chat-pseudo-spinner" />}
              {showPseudoDropdown && pseudoSuggestions.length > 0 && (
                <ul className="admin-chat-pseudo-dropdown" role="listbox">
                  {pseudoSuggestions.map((p) => (
                    <li key={p.pseudo}>
                      <button
                        type="button"
                        onClick={() => {
                          setPseudo(p.pseudoDisplay || p.pseudo);
                          setShowPseudoDropdown(false);
                        }}
                      >
                        <PlayerAvatar pseudo={p.pseudo} size={22} />
                        <span>{p.pseudoDisplay || p.pseudo}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <label className="admin-moderation-field">
            <span>Texte 1</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Contient…" />
          </label>
          <label className="admin-moderation-field">
            <span>Texte 2</span>
            <input value={q2} onChange={(e) => setQ2(e.target.value)} placeholder="Et aussi…" />
          </label>
        </div>
        <div className="admin-chat-log-filters-actions">
          <button type="submit" className="admin-btn primary" disabled={loading || contextLoading}>
            {loading || contextLoading ? 'Chargement…' : 'Rechercher'}
          </button>
          {total > 0 && (
            <span className="admin-chat-log-count">
              {total.toLocaleString('fr-FR')} résultat{total > 1 ? 's' : ''}
              {islands.length > 0 && (
                <> · {islands.length} zone{islands.length > 1 ? 's' : ''} de contexte</>
              )}
            </span>
          )}
        </div>
      </form>

      {error && <div className="admin-empty admin-chat-log-error">{error}</div>}

      {showOccurrenceNav && (
        <div className="admin-chat-hit-nav sticky">
          <button
            type="button"
            className="admin-btn"
            disabled={focusHitIndex <= 0}
            onClick={() => goToHit(focusHitIndex - 1)}
          >
            ← Préc.
          </button>
          <span>
            Occurrence {focusHitIndex + 1} / {hits.length}
          </span>
          <button
            type="button"
            className="admin-btn"
            disabled={focusHitIndex >= hits.length - 1}
            onClick={() => goToHit(focusHitIndex + 1)}
          >
            Suiv. →
          </button>
        </div>
      )}

      {!loading && searched && hits.length === 0 && islands.length === 0 && !error && (
        <div className="admin-empty">Aucun message pour ces critères.</div>
      )}

      {islands.length > 0 && (
        <div className="admin-chat-islands">
          {islands.map((island, islandIndex) => (
            <section key={island.key} className="admin-chat-island">
              {(islandIndex > 0 || island.gapBeforeMs) && (
                <div className="admin-chat-island-gap" role="separator">
                  <span>
                    {island.gapBeforeMs
                      ? `⋯ coupure · ${formatGap(island.gapBeforeMs)} ⋯`
                      : islandIndex > 0
                        ? `⋯ autre table · ${island.natureLabel || island.roomLabel} ⋯`
                        : null}
                  </span>
                </div>
              )}
              <div className="admin-chat-island-header">
                <div className="admin-chat-island-header-main">
                  <strong>
                    {[island.natureLabel, island.stackLabel].filter(Boolean).join(' · ')
                      || island.roomLabel}
                  </strong>
                  {island.tableTitle && (
                    <span className="admin-chat-island-table-title">« {island.tableTitle} »</span>
                  )}
                  {island.participantsLine && (
                    <span className="admin-chat-island-participants">{island.participantsLine}</span>
                  )}
                </div>
                <span>
                  {island.messages.length} msg
                  {island.hitIds.size > 0 && ` · ${island.hitIds.size} résultat${island.hitIds.size > 1 ? 's' : ''}`}
                </span>
              </div>
              <div className="admin-chat-island-feed">
                {island.messages.map((msg, index) => {
                  const key = msgKey(msg);
                  const isHit = island.hitIds.has(key);
                  const canMod = isHit && isModeratable(msg) && !msg.moderatedAt;
                  const canUnredact = isModeratable(msg) && Boolean(msg.moderatedAt);
                  const isFocus = focusHitKey === key;
                  const showAuthor =
                    index === 0 || island.messages[index - 1].pseudo !== msg.pseudo;
                  const ts = new Date(msg.sentAt).getTime();
                  const activeSanctions = activeByPseudo.get(msg.pseudo.toLowerCase()) ?? [];
                  const expiredSanctions = expiredByPseudo.get(msg.pseudo.toLowerCase()) ?? [];
                  const primaryActive = activeSanctions[0];
                  const primaryExpired = !primaryActive ? expiredSanctions[0] : null;
                  const evidenceStatus = evidenceByMessageId.get(msg.messageId);
                  const hasQuote = Boolean(msg.quoteAuthor && msg.quoteText);
                  const isModerated = Boolean(msg.moderatedAt);

                  return (
                    <div
                      key={key}
                      ref={isFocus ? focusRef : undefined}
                      className={[
                        'admin-chat-island-row',
                        isHit ? 'is-hit' : '',
                        isFocus ? 'is-focus' : '',
                        primaryActive ? 'is-sanctioned-author' : '',
                        primaryExpired ? 'is-expired-sanction-author' : '',
                        evidenceStatus === 'active' ? 'is-evidence' : '',
                        evidenceStatus === 'expired' ? 'is-evidence-expired' : '',
                        isModerated ? 'is-moderated' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div
                        className={`admin-chat-island-shell ${
                          canMod || canUnredact ? 'has-actions' : ''
                        }`}
                      >
                        <div className="message message-interactive incoming admin-chat-island-msg">
                          {showAuthor ? (
                            <PlayerAvatar pseudo={msg.pseudo} size={32} className="message-avatar" />
                          ) : (
                            <div className="message-avatar-placeholder" />
                          )}
                          <div className="message-bubble-wrap">
                            {showAuthor && (
                              <div className="admin-chat-island-author">
                                {msg.pseudo}
                                {primaryActive && (
                                  <button
                                    type="button"
                                    className="admin-chat-sanction-badge"
                                    title="Cliquer pour lever la sanction"
                                    onClick={() => openRevokeModal(msg.pseudo)}
                                  >
                                    {sanctionTypeLabel(primaryActive.type)}
                                    {activeSanctions.length > 1 ? ` +${activeSanctions.length - 1}` : ''}
                                  </button>
                                )}
                                {primaryExpired && (
                                  <span
                                    className="admin-chat-sanction-badge is-expired"
                                    title={`Sanction expirée (${formatExpires(primaryExpired.expiresAt)})`}
                                  >
                                    {sanctionTypeLabel(primaryExpired.type)}
                                    {' · expiré'}
                                  </span>
                                )}
                              </div>
                            )}
                            {hasQuote && (
                              <MessageQuoteBlock
                                quote={{
                                  id: `${msg.messageId}-quote`,
                                  author: msg.quoteAuthor!,
                                  text: msg.quoteText!,
                                }}
                                isOutgoing={false}
                              />
                            )}
                            <div className="message-bubble">
                              <div className="message-footer">
                                <span className="message-text">{msg.text}</span>
                                <span className="message-meta">
                                  {msg.source === 'irc' && <span className="source-badge">IRC</span>}
                                  {msg.messageType === 'game_invite' && (
                                    <span className="source-badge">Invitation</span>
                                  )}
                                  {evidenceStatus === 'active' && (
                                    <span
                                      className="admin-chat-evidence-badge"
                                      title="A donné lieu à une sanction active"
                                    >
                                      preuve
                                    </span>
                                  )}
                                  {evidenceStatus === 'expired' && (
                                    <span
                                      className="admin-chat-evidence-badge is-expired"
                                      title="A donné lieu à une sanction désormais expirée"
                                    >
                                      preuve
                                    </span>
                                  )}
                                  {isModerated && (
                                    <span
                                      className="admin-chat-moderated-badge"
                                      title={
                                        msg.moderatedBy
                                          ? `Modéré par ${msg.moderatedBy}`
                                          : 'Message modéré (masqué au public)'
                                      }
                                    >
                                      modéré
                                    </span>
                                  )}
                                  <span className="message-time">{formatChatTime(ts)}</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {canMod && (
                          <div className="admin-chat-hover-bar" role="toolbar" aria-label="Actions de modération">
                            <button
                              type="button"
                              className="admin-chat-hover-btn"
                              title="Mute 10 minutes"
                              onClick={() => void muteUser(msg, 10)}
                            >
                              10m
                            </button>
                            <button
                              type="button"
                              className="admin-chat-hover-btn"
                              title="Mute 1 heure"
                              onClick={() => void muteUser(msg, 60)}
                            >
                              1h
                            </button>
                            <button
                              type="button"
                              className="admin-chat-hover-btn admin-chat-hover-btn--warn"
                              title="Ban canal 7 jours"
                              onClick={() => void banChannel(msg)}
                            >
                              Ban
                            </button>
                            <button
                              type="button"
                              className="admin-chat-hover-btn admin-chat-hover-btn--danger"
                              title="Masquer au public (conservé ici)"
                              onClick={() => void redactMessage(msg)}
                            >
                              <TrashIcon size={14} />
                            </button>
                          </div>
                        )}
                        {canUnredact && (
                          <div className="admin-chat-hover-bar" role="toolbar" aria-label="Restaurer le message">
                            <button
                              type="button"
                              className="admin-chat-hover-btn"
                              title="Dé-modérer (rendre visible au public)"
                              onClick={() => void unredactMessage(msg)}
                            >
                              Restaurer
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="admin-chat-log-pagination">
          <button
            type="button"
            className="admin-btn"
            disabled={offset <= 0 || loading}
            onClick={() => void runSearch(Math.max(0, offset - limit))}
          >
            ← Précédent
          </button>
          <span>Page {page} / {totalPages}</span>
          <button
            type="button"
            className="admin-btn"
            disabled={offset + limit >= total || loading}
            onClick={() => void runSearch(offset + limit)}
          >
            Suivant →
          </button>
        </div>
      )}

      {revokeTarget && (
        <div
          className="admin-chat-modal-backdrop"
          role="presentation"
          onClick={() => !revoking && setRevokeTarget(null)}
        >
          <div
            className="admin-chat-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-chat-revoke-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="admin-chat-revoke-title">Lever la sanction ?</h3>
            <p>
              Joueur <strong>{revokeTarget.pseudo}</strong>
              {' — '}
              {revokeTarget.sanctions.length} sanction
              {revokeTarget.sanctions.length > 1 ? 's actives' : ' active'}.
            </p>
            <ul className="admin-chat-revoke-list">
              {revokeTarget.sanctions.map((s) => (
                <li key={s.id}>
                  <strong>{sanctionTypeLabel(s.type)}</strong>
                  {' · '}
                  {SCOPE_LABELS[s.scope] ?? s.scope}
                  {' · expire '}
                  {formatExpires(s.expiresAt)}
                  {s.reason ? ` · ${s.reason}` : ''}
                  {s.issuedBy ? ` · par ${s.issuedBy}` : ''}
                </li>
              ))}
            </ul>
            <div className="admin-chat-modal-actions">
              <button
                type="button"
                className="admin-btn"
                disabled={revoking}
                onClick={() => setRevokeTarget(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="admin-btn primary"
                disabled={revoking}
                onClick={() => void confirmRevoke()}
              >
                {revoking ? 'Levée…' : 'Confirmer la levée'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
